import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AuthService} from "../../services/auth/auth.service";
import {
  AuthControllerService,
  BillingControllerService,
  Organisation,
  OrganisationControllerService,
  Price,
  Product,
  ProductDto,
  User
} from "../../api";
import {StateConfigService} from "../../services/state/state-config.service";
import {Subscription} from "rxjs";
import {MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef} from "@angular/material/dialog";

import {CheckoutPriceCardComponent} from "./checkout-price-card/checkout-price-card.component";
import {
  BicButtonComponent,
  BicPopupComponent,
  BicToggleComponent,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faCartShopping, faExclamationCircle} from "@fortawesome/pro-solid-svg-icons";
import {CheckoutInfoComponent} from "./checkout-info/checkout-info.component";
import {PageHeaderComponent} from "../util/design/page-header/page-header.component";
import {CloudNumberComponent} from "../util/design/cloud-number/cloud-number.component";

enum PriceType {
  YEARLY = 'yearly',
  MONTHLY = 'monthly',
  ADDON = 'addon',
}

/** Ein Abo-Modell samt dem Stripe-Preis des aktuellen Intervalls. */
interface PlanCard {
  key: string;
  product: ProductDto;
  price?: Price;
}

@Component({
  selector: 'app-shop',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicToggleComponent,
    TooltipDirective,
    FaIconComponent,
    CheckoutPriceCardComponent,
    CheckoutInfoComponent,
    PageHeaderComponent,
    CloudNumberComponent,
  ]
})
export class CheckoutComponent implements OnInit, OnDestroy {
  stateConfigService = inject(StateConfigService);
  organisationControllerService = inject(OrganisationControllerService);
  dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private authControllerService = inject(AuthControllerService);
  private billingControllerService = inject(BillingControllerService);

  showInfo: boolean = false;

  user!: User;
  subscribedPrices: Price[] = [];
  prices: Price[] = [];
  subscribedProducts: Product[] = [];

  products: ProductDto[] = [];

  priceType: PriceType = PriceType.MONTHLY;

  updateSub: Subscription | undefined;
  organisations: Organisation[] = [];

  ngOnInit(): void {
    // Bewusst KEIN selectProject(undefined): der Shop zeigt Abo und Kontingent auf Organisations-
    // bzw. Persoenlich-Ebene, das Projekt ist hier irrelevant. Frueher wurde die Projektauswahl
    // dafuer global geleert -- der Nutzer stand nach einem Shop-Besuch ohne Projekt da (und
    // getProjectId() lieferte 0, was Folge-Calls auf 404 laufen liess). Der org-weite Scope kommt
    // jetzt aus <app-shop-info>, das seine Zaehlungen explizit ohne projectId abfragt.
    if(this.stateConfigService.selectedOrganisation == undefined){
      this.stateConfigService.isPersonal = true;
    }

    this.authControllerService.profile().subscribe(next => {
      this.user = next;

      this.organisationControllerService.getOrganisations().subscribe(organisations => {
        this.organisations = organisations;
      })
    });

    if (this.stateConfigService.contingent) {
      this.update();
    } else {
      this.updateSub = this.stateConfigService.updated.subscribe(next => {
        this.update();
        this.updateSub?.unsubscribe();
      });
    }
  }

  ngOnDestroy(): void {
    this.updateSub?.unsubscribe();
  }

  /**
   * Die Karten der Shop-Seite: produkt- und nicht preisgetrieben, damit Pläne ohne Stripe-Preis
   * (Enterprise) erscheinen können und die Reihenfolge aus `sortOrder` der DB kommt.
   */
  get planCards(): PlanCard[] {
    const interval = this.isYearly ? 'year' : 'month';

    return this.products
      .map(product => ({
        key: product.id!,
        product: product,
        price: this.priceFor(product, interval)
      }))
      .filter(card => card.product.contactOnly || card.price != undefined)
      .sort((a, b) => (a.product.sortOrder ?? 100) - (b.product.sortOrder ?? 100));
  }

  /**
   * Der Preis des laufenden Abos. Bewusst ohne Filter auf `price.active`: In Stripe heißt
   * `active: false` nur "nicht mehr neu buchbar" – bestehende Abos auf archivierten Preisen
   * laufen weiter und müssen als Abo erkannt werden.
   */
  get activePrice(): Price {
    return this.subscribedPrices.find(price => price.type == 'recurring') ?? this.subscribedPrices[0];
  }

  productSettingsBy(price: Price) {
    return this.products.find(product => product.id == price.product);
  }

  private priceFor(product: ProductDto, interval: string): Price | undefined {
    const recurring = this.prices.filter(price => price.product == product.id && price.type == 'recurring');

    // Die kostenlose Testphase hat in Stripe nur einen Preis – ohne diesen Fallback würde die
    // Karte beim Umschalten auf "Jährlich" verschwinden.
    return recurring.find(price => price.recurring?.interval == interval)
      ?? (product.isTrial ? recurring[0] : undefined);
  }

  get mail() {
    return this.user.mail;
  }

  isSpecial(price: Price) {
    return price.active == false;
  }
  protected readonly faCartShopping = faCartShopping;
  protected readonly faExclamationCircle = faExclamationCircle;

  get onetime() {
    return this.prices
      .filter(price => price.type == 'one_time')
      .sort((a, b) => a.unitAmount! - b.unitAmount!);
  }

  update() {
    this.billingControllerService.getPrices(this.stateConfigService.selectedOrganisation?.id ?? undefined).subscribe(next => {
      this.prices = next;

      this.billingControllerService.getSubscriptions(this.stateConfigService.selectedOrganisation?.id ?? undefined).subscribe(next => {
        this.subscribedPrices = next;

        this.subscribedProducts = this.subscribedPrices
          .map(price => price.productObject!);

        const active = this.activePrice;
        if (active?.type === 'recurring') {
          this.priceType = active.recurring?.interval === 'year'
            ? PriceType.YEARLY
            : PriceType.MONTHLY;
        }
      });
    });

    this.billingControllerService.getProducts().subscribe(next => {
      this.products = next;
    });
  }

  oneTimeBy(price: Price) {
    return this.onetime.find(p => p.product == price.product);
  }


  intervalBy(price: Price) {
    return price.recurring?.interval == 'year' ? 'Jährlich' : 'Monatlich';
  }

  intervalByProduct(product: Product) {

    const price = this.subscribedPrices.find(price => price.product == product.id);

    return this.intervalBy(price!);
  }

  get activeAbonnementName(): string {
    return this.subscribedProducts.map(product => `${product.name} (${this.intervalByProduct(product)})`).join(', ');
  }


  checkout(price: Price) {
    const dialogRef = this.dialog.open(CheckoutConfirmationDialog, {
      panelClass: 'rounded-corners-dialog',
      width: '750px',
      data: {
        stateConfigService: {
          ...this.stateConfigService,
          productName: price.productObject?.name
        }
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (this.subscribedProducts.length != 0 && !this.onetime.map(onetime => onetime.id).includes(price.id)) {
          this.billingControllerService.getPortalUrl(this.stateConfigService.selectedOrganisation?.id ?? undefined).subscribe(next => {
            window.open(next.url)
          });
        }else {
          this.billingControllerService.checkout({priceId: price.id!}, this.stateConfigService.selectedOrganisation?.id ?? undefined).subscribe(next => {
            window.open(next.url)
          })
        }
      }
    });



  }

  get isYearly() {
    return this.priceType == PriceType.YEARLY;
  }

  get isMonthly() {
    return this.priceType == PriceType.MONTHLY;
  }

  get isAddon() {
    return this.priceType == PriceType.ADDON;
  }

  openCustomerPortal(){
    this.billingControllerService.getPortalUrl().subscribe(res => {
      window.open(res.url, '_blank');
    });
  }

  protected readonly PriceType = PriceType;

  togglePriceType($event: Event) {

  }

  /**
   * Vergleich über Produkt und Intervall statt über die Price-ID: Die Karte zeigt den aktuellen
   * Katalogpreis, das Abo kann aber auf einem älteren, inzwischen archivierten Preis desselben
   * Plans laufen.
   */
  isActivated(price: Price) {
    return this.subscribedPrices.some(subscribed =>
      subscribed.id == price.id
      || (subscribed.type == 'recurring'
        && price.type == 'recurring'
        && subscribed.product == price.product
        && subscribed.recurring?.interval == price.recurring?.interval));
  }

  get activeProduct(): Product {
    return this.subscribedProducts.filter(product => product.active)[0];
  }
}


@Component({
  selector: 'app-checkoutConfirmationDialog',
  templateUrl: './checkout-confirmation-dialog.html',
  styleUrls: ['./checkout.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogClose,
    BicPopupComponent,
    BicButtonComponent
  ]
})
export class CheckoutConfirmationDialog implements OnInit {
  data = inject<{
    stateConfigService: any;
  }>(MAT_DIALOG_DATA);
  protected dialogRef = inject<MatDialogRef<CheckoutConfirmationDialog>>(MatDialogRef);

  productName: string;
  selectedOrganisation: string;

  constructor() {
    const data = this.data;

    this.productName = data.stateConfigService.productName;
    this.selectedOrganisation = data.stateConfigService.selectedOrganisation?.name ?? 'Persönlich';
  }

  get title() {
    return `Abo für Profil "${this.selectedOrganisation}" kaufen`;
  }

  goBuy() {
    this.dialogRef.close(true);
  }

  ngOnInit() {
  }
}
